/**
 * serviceLayerClient.headers — request-header assembly for serviceFetch.
 *
 * Extracted from serviceLayerClient.ts (LOC ceiling, behaviour-preserving).
 * Owns the full `baseHeaders` build: Content-Type, request-id, actor/org,
 * HMAC, impersonation, the Bearer-JWT iOS-Safari-ITP fallback (incl. the
 * non-fatal stale-token recycle), CSRF, and the dev/test bypass header.
 *
 * The cookie path stays primary in serviceFetch (`credentials:'include'`);
 * this only augments the outgoing headers. All env-derived flags are passed in
 * via `BuildHeadersEnv` so the single source of `import.meta.env` reads stays
 * in serviceLayerClient.ts.
 */

import { getCookie } from './cookies';
import { buildHmacHeaders } from './httpSigning';
import {
  generateRequestId,
  getActorEmailFromGlobal,
} from './httpHelpers';
import { isMultipartBody, bodyForHmacSigning } from './serviceLayerClient.body';
import { getFreshAccessToken } from '../auth/authSession';
import {
  getSupabaseAccessTokenFromStorage,
  isAccessTokenStale,
} from './serviceLayerClient.auth';
import type { RequestContext } from './serviceLayerClient.types';

/** Env-derived flags + secrets the header build needs (read once upstream). */
export type BuildHeadersEnv = {
  DEBUG_HTTP: boolean;
  ENABLE_AUTH_DEBUG: boolean;
  TEST_BYPASS: boolean;
  TEST_ACTOR_ENV: string | undefined;
  HMAC_SECRET: string | undefined;
  HMAC_ACTOR: string | undefined;
};

export type BuildHeadersResult = {
  headers: Record<string, string>;
  /** Resolved request id (echoed back into the response fallback). */
  requestId: string;
};

/**
 * Build the outgoing request headers for one serviceFetch call. Async because
 * the Bearer-JWT fallback may recycle a stale token via getFreshAccessToken().
 */
export async function buildRequestHeaders(
  path: string,
  method: string,
  init: RequestInit & { ctx?: RequestContext },
  env: BuildHeadersEnv,
): Promise<BuildHeadersResult> {
  const ctx = init.ctx || {};
  const { DEBUG_HTTP, ENABLE_AUTH_DEBUG } = env;

  // Content-Type only applies to JSON-shaped bodies; FormData/Blob need
  // the browser to set `multipart/form-data; boundary=...` themselves.
  const multipart = isMultipartBody(init.body);
  const baseHeaders: Record<string, string> = {
    ...(multipart ? {} : { 'Content-Type': 'application/json' }),
    ...(init.headers as Record<string, string> | undefined),
  };
  // Ensure request id
  const initialRequestId = ctx.requestId || generateRequestId();
  baseHeaders['X-Request-Id'] = initialRequestId;
  // Inject actor email if available (for logging/debugging)
  const actorEmail = ctx.actorEmail || getActorEmailFromGlobal() || env.TEST_ACTOR_ENV || undefined;
  if (actorEmail) baseHeaders['X-Actor-Email'] = actorEmail;
  if (ctx.orgId) baseHeaders['X-Org-Id'] = ctx.orgId;

  const bodyStringForHmac = bodyForHmacSigning(init.body);

  const hmacHeaders = await buildHmacHeaders(env.HMAC_SECRET, env.HMAC_ACTOR, bodyStringForHmac);
  if (hmacHeaders) Object.assign(baseHeaders, hmacHeaders);

  applyImpersonationHeader(baseHeaders, DEBUG_HTTP, ENABLE_AUTH_DEBUG);
  await applyBearerFallback(baseHeaders, DEBUG_HTTP, ENABLE_AUTH_DEBUG);
  applyCsrfHeader(baseHeaders, method, DEBUG_HTTP, ENABLE_AUTH_DEBUG);

  // Dev/test bypass header when explicit bypass flag is set
  if (env.TEST_BYPASS && actorEmail) {
    baseHeaders['X-Test-Actor'] = actorEmail;
  }

  return { headers: baseHeaders, requestId: initialRequestId };
}

/**
 * Headers for the ONE re-send after a successful 401 cookie-handoff. The handoff re-issues the SL
 * cookies — csrf_token included — and may have recycled the Supabase JWT, so the first attempt's
 * X-CSRF-Token and Bearer are stale: re-sending them draws a 403 CSRF_FAILED on the very request the
 * refresh existed to save. Everything is rebuilt from the live sources EXCEPT the request id, which is
 * preserved so the retry stays one logical request in the SL logs and in the caller's error.
 */
export async function rebuildHeadersAfterHandoff(
  path: string,
  method: string,
  init: RequestInit & { ctx?: RequestContext },
  env: BuildHeadersEnv,
  requestId: string,
): Promise<Record<string, string>> {
  const rebuilt = await buildRequestHeaders(path, method, { ...init, ctx: { ...init.ctx, requestId } }, env);
  return rebuilt.headers;
}

/** Impersonation support: read from sessionStorage and send as header. */
function applyImpersonationHeader(
  baseHeaders: Record<string, string>,
  DEBUG_HTTP: boolean,
  ENABLE_AUTH_DEBUG: boolean,
): void {
  try {
    const impersonationRaw = typeof window !== 'undefined' ? sessionStorage.getItem('scout.impersonation.v1') : null;
    if (impersonationRaw) {
      const impersonation = JSON.parse(impersonationRaw);
      if (impersonation?.email) {
        baseHeaders['X-Impersonate-User'] = impersonation.email;
        if (DEBUG_HTTP || ENABLE_AUTH_DEBUG) {
          console.warn(`[service-client] Impersonating user: ${impersonation.email}`);
        }
      }
    }
  } catch (e) {
    // Ignore sessionStorage errors (SSR, private browsing, etc.)
    if (DEBUG_HTTP) console.warn('[service-client] Failed to read impersonation from sessionStorage:', e);
  }
}

/**
 * Bearer JWT fallback for browsers that block / partition the HttpOnly
 * cookies set by `/v1/auth/cookie-handoff` (iOS Safari ITP — empirically
 * confirmed for Steffen 2026-05-26 via bug-report `auth.hasSession:true`
 * in localStorage but `/v1/views/...` 401'ing → empty missions). Cookies
 * remain the primary auth signal (credentials:'include'); the Bearer header
 * makes the request auth'd even when the cookie path is broken at the
 * transport layer. SL middleware accepts JWT via either cookie OR
 * Authorization header per SL-247S2 annotation on
 * BUG-SL-SCOUT-REP-CANNOT-READ-OWN-MISSIONS-2026-05-26.
 *
 * Skip if the caller already set Authorization explicitly (tests + special
 * cases stay in control). Server-side: if both cookie and Bearer are
 * present, both should reference the same user JWT — no conflict.
 *
 * Case-insensitive header check (per RFC 7230 §3.2 / fetch Headers
 * normalization): a caller passing `authorization` (lowercase, which is
 * valid HTTP) would otherwise bypass this guard, get a second
 * `Authorization` header appended, and fetch would merge them into a
 * comma-separated value — producing an invalid Bearer token (Codex P2
 * #1772). Iterate keys with toLowerCase() so any casing variant counts.
 */
async function applyBearerFallback(
  baseHeaders: Record<string, string>,
  DEBUG_HTTP: boolean,
  ENABLE_AUTH_DEBUG: boolean,
): Promise<void> {
  const hasAuthHeader = Object.keys(baseHeaders).some(
    (k) => k.toLowerCase() === 'authorization',
  );
  if (hasAuthHeader) return;

  const accessToken = getSupabaseAccessTokenFromStorage();
  if (accessToken && !isAccessTokenStale(accessToken)) {
    // Fast path: a valid, non-expiring token is forwarded synchronously with
    // no refresh round-trip — no perf regression on normal requests.
    baseHeaders['Authorization'] = `Bearer ${accessToken}`;
    return;
  }
  // Stale, near-expiry, malformed, or absent token. A RAW localStorage read
  // never triggers Supabase auto-refresh, so without this the expired JWT
  // would keep being sent — degraded/empty 200s and 403s then surface as
  // silent blank pages (BUG-FE-SILENT-SESSION-JWT-DEGRADATION-2026-05-31).
  // getFreshAccessToken() routes through the canonical SL Supabase client
  // (authClient, autoRefreshToken:true) so the token is recycled.
  //
  // CRITICAL: recycling MUST be non-fatal. getFreshAccessToken() can throw
  // (offline, revoked refresh token, or an unconfigured stub SL client
  // whose `auth` has no refreshSession) — when it did, the throw propagated
  // out of serviceFetch and rejected EVERY request, blanking missions /
  // calling / registry / prequal all at once (the 2026-06-01 all-dark
  // regression, #1793). On any failure we fall through to the cookie path
  // (credentials:'include') and let the server's RESPONSE be authoritative.
  let freshToken: string | null = null;
  try {
    freshToken = await getFreshAccessToken();
  } catch (refreshErr) {
    if (DEBUG_HTTP || ENABLE_AUTH_DEBUG) {
      console.warn('[service-client] stale-JWT recycle failed; falling through to cookie path', refreshErr);
    }
  }
  if (freshToken) {
    baseHeaders['Authorization'] = `Bearer ${freshToken}`;
  }
  // If recycle yielded no token we do NOT pre-emptively blank the app: the
  // HttpOnly cookie may still be valid and the request may succeed. We also
  // do not attach the known-stale token. The session-expiry overlay is
  // driven ONLY by an authoritative 401 (or an explicit expiry envelope) on
  // the RESPONSE below — never speculatively here, which previously blanked
  // sessions that were actually still valid via the cookie path (#1793).
}

/** Add CSRF token for non-GET requests (cookie-based auth). */
function applyCsrfHeader(
  baseHeaders: Record<string, string>,
  method: string,
  DEBUG_HTTP: boolean,
  ENABLE_AUTH_DEBUG: boolean,
): void {
  if (method === 'GET' || method === 'HEAD' || method === 'OPTIONS') return;
  const csrfToken = getCookie('csrf_token');
  if (csrfToken) {
    baseHeaders['X-CSRF-Token'] = csrfToken;
    if (DEBUG_HTTP || ENABLE_AUTH_DEBUG) {
      console.warn(`[service-client] Including CSRF token for ${method} request`);
    }
  }
  // No token → don't throw; let the request proceed and Service Layer return
  // 403 CSRF_FAILED so the caller handles it with proper context.
}
